'use client';

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useConversation } from '@kit/chat/hooks';
import { BALLEE_USER_ID } from '@kit/chat/lib';
import { dancerShortUrl } from '@kit/shared/handles';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { ADMIN_PATHS } from '~/admin/_lib/constants';
import { useIsSuperAdmin } from '~/lib/hooks/use-is-super-admin';

export interface ParticipantProfileLink {
  href: string;
  /** The public profile lives on the ballee.co apex, outside the inbox app. */
  external: boolean;
}

interface ConversationMembers {
  participants?: Array<{ user_id: string }>;
  my_participation?: unknown;
}

/**
 * Where clicking a conversation member's avatar goes.
 *
 * - Super admins: the admin dancer page, keyed by user id. It redirects to
 *   the account page for a member without a dancer profile.
 * - Everyone else: the member's public dancer profile, when they have one
 *   that is public. Otherwise no link: a private profile or a non-dancer has
 *   no page this viewer may open.
 *
 * The handles come from ONE query per conversation, over every participant
 * but the viewer. The ids are read from the cached conversation, so the
 * header and the message list ask for the same set and share the result
 * (no per-avatar and no per-surface round-trip).
 * professional_profiles_authenticated_select already lets any signed-in user
 * read a public profile, so this reads nothing new.
 *
 * `masked` is true where the viewer sees an identity instead of people (the
 * support brand, an org seen from outside). Nothing is looked up then, so
 * the people behind it never reach the network, and nothing links. Callers
 * still resolve only the ids they show as themselves.
 */
export function useParticipantProfileLinks(
  conversationId: string,
  { masked }: { masked: boolean },
) {
  const client = useSupabase();
  const { data: isSuperAdmin } = useIsSuperAdmin();
  const { data: conversation } = useConversation(conversationId);

  const ids = useMemo(() => {
    const members = conversation as ConversationMembers | null | undefined;
    if (!members?.participants) return [];
    const viewerId = (members.my_participation as { user_id?: string } | null)
      ?.user_id;
    return Array.from(
      new Set(
        members.participants
          .map((p) => p.user_id)
          .filter((id) => !!id && id !== BALLEE_USER_ID && id !== viewerId),
      ),
    ).sort();
  }, [conversation]);

  const { data: handles } = useQuery({
    queryKey: ['participant-public-handles', conversationId, ids],
    queryFn: async () => {
      const { data, error } = await client
        .from('professional_profiles')
        .select('user_id, handle')
        .in('user_id', ids)
        .eq('profile_type', 'dancer')
        .eq('is_profile_public', true)
        .not('archived', 'is', true)
        .not('handle', 'is', null);

      if (error) throw error;

      const byUser = new Map<string, string>();
      for (const row of data ?? []) {
        if (row.user_id && row.handle) byUser.set(row.user_id, row.handle);
      }
      return byUser;
    },
    // Super admins link by user id and never need the handles.
    enabled: !masked && isSuperAdmin === false && ids.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  return useCallback(
    (userId: string | null | undefined): ParticipantProfileLink | null => {
      if (masked || !userId || userId === BALLEE_USER_ID) return null;
      if (isSuperAdmin) {
        return { href: ADMIN_PATHS.DANCERS.DETAIL(userId), external: false };
      }
      const handle = handles?.get(userId);
      return handle ? { href: dancerShortUrl(handle), external: true } : null;
    },
    [handles, isSuperAdmin, masked],
  );
}
